import { useEffect, useState } from "react";

import { useAuth } from "@/context/AuthContext";
import { agoraRefVisit, agoraRefConvert } from "@/lib/api";
import {
  CON_PERMISO, guarda, hayQuePreguntar, lee, olvida, puede,
} from "@/lib/consentimiento.mjs";

// RefTracker Y SU PREGUNTA SALEN DE `JuegoDelMundo.jsx` EL 19 DE OCTUBRE DE 2026, y el motivo
// es el peso de la primera carga. Esto lo monta `App.js` en todas las paginas -tiene que estar
// mirando por si alguien llega con un `?ref=`-, asi que arrastraba al bulto inicial el fichero
// entero del juego del Mundo: las misiones de la semana, la Liga, el boton de compartir y, con
// ellos, `FilaDeLaLiga` y cuatro iconos. Catorce kilobytes de fuente que no pinta nadie hasta
// que el visitante entra en el Mundo.
//
// NO ES UNA OPTIMIZACION DE LAS DE POR SI ACASO: la portada viaja entera en la primera carga y
// media 257,7 kB de los 260 que permite `check_presupuesto`. Lo que se gana aqui es sitio para
// los bloques que le faltan a la portada, y se gana separando dos cosas que nunca tuvieron que
// estar juntas: un contador que vigila la direccion, y un panel de juego.
//
// LO QUE NO CAMBIA es el comportamiento, y por eso esto es una mudanza y no un arreglo: el
// mismo codigo, los mismos `data-testid` y el mismo permiso. `tools/prueba_referidos.py` y
// `prueba_consentimiento.mjs` siguen valiendo tal cual.

/**
 * La pregunta, y solo cuando hay algo que preguntar.
 *
 * No es un cartel de cookies: no sale en todas las visitas, sale cuando alguien llega por
 * el enlace de otro alumno, que es el único momento en que hace falta guardar algo que no
 * es necesario para estudiar. Quien entra por su cuenta no ve nada, porque no hay nada que
 * consentir.
 */
function PermisoReferido({ onResponde }) {
  const t = CON_PERMISO.referidos;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-alpha-black/95 backdrop-blur px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      role="dialog"
      aria-label={t.titulo}
      data-testid="permiso-referidos"
    >
      <div className="mx-auto max-w-2xl flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-serif-display text-base text-white leading-tight">{t.titulo}</p>
          <p className="text-[13px] text-white/60 leading-snug mt-1">{t.dice}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {/* El «no» va primero y con el mismo peso visual que el «sí». Un botón de rechazo
              escondido o en gris claro convierte el permiso en un trámite, y entonces no es
              un permiso. */}
          <button
            type="button"
            onClick={() => onResponde(false)}
            className="px-4 py-2 rounded-full border border-white/20 text-white/80 font-mono-alpha text-[11px] uppercase tracking-widest hover:bg-white/[0.06]"
            data-testid="permiso-referidos-no"
          >
            {t.no}
          </button>
          <button
            type="button"
            onClick={() => onResponde(true)}
            className="px-4 py-2 rounded-full bg-alpha-red text-white font-mono-alpha text-[11px] uppercase tracking-widest hover:bg-alpha-red/85"
            data-testid="permiso-referidos-si"
          >
            {t.si}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Captura `?ref=...` y convierte el registro del invitado en reliquia para quien compartió.
 *
 * ANTES ESCRIBIA PRIMERO Y NO PREGUNTABA NUNCA. La politica de cookies decia de estas tres
 * marcas que «solo se activan si dices que sí», y aquí se guardaban en cuanto llegaba un
 * `?ref=`. Ahora el código se queda EN MEMORIA hasta que haya respuesta: si la respuesta es
 * que no, no llega a tocarse el navegador y no se manda la visita al servidor.
 */
export function RefTracker() {
  const { user, ready } = useAuth();
  const [permisos, setPermisos] = useState(() => lee(window.localStorage));
  // El código se lee una vez y no se guarda: vive aquí mientras se pregunta.
  const [codigo] = useState(() => new URLSearchParams(window.location.search).get("ref"));
  const permitido = puede(permisos, "referidos");
  const preguntar = Boolean(codigo) && hayQuePreguntar(permisos, "referidos");

  // Limpieza de lo que quedara de antes de que esto se preguntara. Sin ella, un navegador
  // que ya tenía las marcas puestas seguiría con ellas para siempre: el permiso empezaría a
  // pedirse a los nuevos y los de antes se quedarían como estaban, que es la peor mitad.
  useEffect(() => {
    if (hayQuePreguntar(permisos, "referidos") && !codigo) {
      olvida(window.localStorage, "referidos");
    }
  }, [permisos, codigo]);

  // La visita. Solo con permiso, y solo entonces se escribe el visitante anónimo estable.
  useEffect(() => {
    if (!codigo || !permitido) return;
    localStorage.setItem("alpha_ref", codigo);
    let visitor = localStorage.getItem("alpha_visitor");
    if (!visitor) {
      visitor = `v-${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      localStorage.setItem("alpha_visitor", visitor);
    }
    agoraRefVisit(codigo, visitor).catch(() => {});
  }, [codigo, permitido]);

  // La conversión: un usuario con sesión que llegó por un enlace ajeno.
  useEffect(() => {
    if (!ready || !user || !permitido) return;
    const code = localStorage.getItem("alpha_ref");
    if (!code || code === user.id || localStorage.getItem("alpha_ref_claimed")) return;
    agoraRefConvert(code)
      .then(() => localStorage.setItem("alpha_ref_claimed", "1"))
      .catch(() => localStorage.setItem("alpha_ref_claimed", "1"));
  }, [ready, user, permitido]);

  if (!preguntar) return null;
  return (
    <PermisoReferido
      onResponde={(r) => setPermisos(guarda(window.localStorage, "referidos", r))}
    />
  );
}
